import { ColDef, ICellRendererParams, ValueSetterParams } from '@ag-grid-community/core';

import { AgIconLinkCellComponent } from '@shared/ag-components/ag-icon-link-cell/ag-icon-link-cell.component';
import {
  AgDropdownViewComponent,
  AgDropdownViewComponentParams,
} from '@shared/components/ag-dropdown-view/ag-dropdown-view.component';
import { EDITABLE_CELL, EDITABLE_HEADER } from '@shared/constants/ag-classes';
import { Currency } from '@shared/services/gql.service';
import { Utils } from '@shared/utils/utils';

import { FREQUENCY_OPTIONS, MAP_FREQUENCY_TITLE_BY_TYPE } from '../const/frequency.const';
import { SiteBudgetGridContext } from '../types/site-budget-grid-context';
import { GridRowData } from '../types/site-budget-grid-row';
import { ignoreCompareForNewRows } from '../utils/site-budget-grid.utils';

export const SITE_BUDGET_HIDDEN_COLUMNS: ColDef<GridRowData>[] = [
  {
    field: 'id',
    hide: true,
  },
  {
    field: 'paymentScheduleId',
    hide: true,
  },
  {
    field: 'currency',
    hide: true,
  },
];

export const siteBudgetColumns = (cxt: SiteBudgetGridContext): ColDef<GridRowData>[] => [
  ...(cxt.isSiteInvoiceables ? [getCheckboxColumn(cxt)] : []),
  getDescriptionColumn(cxt),
  ...(!cxt.isSiteInvoiceables ? [getOptionalColumn(cxt)] : []),
  getAmountColumn(cxt),
  getNotesColumn(cxt),
  ...(cxt.isSiteInvoiceables ? [getFrequencyColumn(cxt)] : []),
];

const getCheckboxColumn = (cxt: SiteBudgetGridContext): ColDef<GridRowData> => ({
  cellClass: () => {
    const isEditable = cxt.editMode && cxt.isSiteInvoiceables;
    const classes = ['relative', '!px-[10px]', '!pt-0'];

    if (isEditable) {
      classes.push(EDITABLE_CELL);
    }

    return classes;
  },
  checkboxSelection: cxt.editMode,
  editable: false,
  headerCheckboxSelection: cxt.editMode,
  headerClass: ['font-normal', EDITABLE_HEADER],
  hide: !cxt.editMode,
  maxWidth: 40,
  minWidth: 40,
  sortable: false,
  suppressFillHandle: true,
  width: 40,
});

const getDescriptionColumn = (cxt: SiteBudgetGridContext): ColDef<GridRowData> => {
  const isEditable = cxt.editMode && cxt.isSiteInvoiceables;

  return {
    cellClass: () => (isEditable ? EDITABLE_CELL : ''),
    cellClassRules: {
      'border-aux-error': (params) => !!Object.keys(params.data?.errors || {}).length,
    },
    comparator: ignoreCompareForNewRows('text'),
    editable: isEditable,
    field: 'name',
    headerClass: () => (isEditable ? EDITABLE_HEADER : ''),
    headerName: 'Description',
    minWidth: 120,
    tooltipValueGetter: Utils.getDuplicateNameCellTooltip,
  };
};

const getAmountColumn = (cxt: SiteBudgetGridContext): ColDef<GridRowData> => ({
  cellClass: () => (cxt.editMode ? EDITABLE_CELL : ''),
  cellDataType: 'text',
  comparator: ignoreCompareForNewRows('number'),
  editable: cxt.editMode,
  field: 'amount',
  headerClass: () => (cxt.editMode ? EDITABLE_HEADER : ''),
  headerValueGetter: () => `Amount (${cxt.currency})`,
  minWidth: 90,
  valueFormatter: (params) => Utils.agCurrencyFormatter(params, cxt.currency || Currency.USD),
  valueParser: (params) => {
    return params.newValue === params.oldValue
      ? params.oldValue
      : Utils.castStringToNumber(params.newValue).toString();
  },
});

const getNotesColumn = (cxt: SiteBudgetGridContext): ColDef<GridRowData> => ({
  autoHeight: true,
  cellClass: () => (cxt.editMode ? EDITABLE_CELL : ''),
  cellEditor: 'agLargeTextCellEditor',
  cellStyle: { 'white-space': 'pre-wrap' },
  comparator: ignoreCompareForNewRows('text'),
  editable: cxt.editMode,
  field: 'note',
  headerClass: () => (cxt.editMode ? EDITABLE_HEADER : ''),
  headerName: 'Notes',
  minWidth: 200,
  tooltipField: 'note',
  wrapText: true,
});

const getOptionalColumn = (cxt: SiteBudgetGridContext): ColDef<GridRowData> => ({
  cellRenderer: AgIconLinkCellComponent,
  cellRendererParams: (params: ICellRendererParams) => ({
    hideText: true,
    icons: [
      {
        icon: 'Check',
        iconClass: 'text-aux-green-dark',
        showIcon: params.value,
      },
    ],
    justifyCenter: true,
  }),
  editable: false,
  field: 'optional',
  headerName: 'Optional',
  hide: cxt.isSiteInvoiceables,
  maxWidth: 105,
  minWidth: 105,
  resizable: false,
});

const getFrequencyColumn = (cxt: SiteBudgetGridContext): ColDef<GridRowData> => ({
  cellClass: () => (cxt.editMode ? EDITABLE_CELL : ''),
  cellEditor: 'agRichSelectCellEditor',
  cellEditorParams: () => {
    return {
      cellHeight: 32,
      values: FREQUENCY_OPTIONS,
    };
  },
  cellRenderer: AgDropdownViewComponent,
  cellRendererParams: <AgDropdownViewComponentParams>{
    disabled: !cxt.editMode,
  },
  comparator: ignoreCompareForNewRows('frequency'),
  editable: cxt.editMode,
  field: 'frequency',
  headerClass: () => (cxt.editMode ? EDITABLE_HEADER : ''),
  headerName: 'Frequency',
  minWidth: 300,
  valueFormatter: (node) => MAP_FREQUENCY_TITLE_BY_TYPE.get(node.value) || '',
  valueSetter: (params: ValueSetterParams) => {
    const [valueFromDragEvent] =
      [...MAP_FREQUENCY_TITLE_BY_TYPE.entries()].find(([, label]) => label === params.newValue) ||
      [];

    const valueFromDropdown = FREQUENCY_OPTIONS.find((val) => val === params.newValue);

    if (params.newValue !== '' && params.newValue !== null) {
      if (!valueFromDragEvent && !valueFromDropdown) {
        return false;
      }
    }

    params.data.frequency = valueFromDragEvent || valueFromDropdown;

    return true;
  },
});
